import type { QueryClient } from "@tanstack/react-query";
import { api, unwrap } from "@/shared/api/client";
import type { components } from "@/shared/api/generated/schema";
import { invalidate, qk, type Slice } from "@/shared/api/keys";

type S = components["schemas"];

export type SyncBoundary = S["CursorOut"];
export type SyncChange = S["ChangeOut"];
export type SyncChangePage = S["ChangePageOut"];

/**
 * One central translation between the server's stable wire entities and the
 * coarse React Query slices they can make stale.  Composite screens are
 * intentionally included: a changed task also changes Today, a Project and
 * review read models through the existing slice map.
 */
export const SYNC_ENTITY_SLICES: Readonly<Record<string, readonly Slice[]>> = {
  page: ["pages"],
  page_revision: ["pages"],
  page_preference: ["pages"],
  project: ["projects", "relations"],
  goal: ["projects", "compass", "relations"],
  decision: ["projects", "compass", "relations"],
  decision_option: ["projects", "compass"],
  task: ["tasks", "schedule", "planning", "relations"],
  task_deferral: ["tasks", "schedule"],
  routine: ["routines", "tasks", "schedule", "planning", "relations"],
  schedule_block: ["schedule", "tasks", "planning", "relations"],
  plan: ["planning", "tasks", "routines", "projects", "schedule", "relations"],
  focus_session: ["focus", "tasks", "activity"],
  focus_interruption: ["focus", "activity"],
  note: ["notes", "memory", "projects", "relations"],
  note_revision: ["notes", "memory"],
  draft: ["inbox", "memory"],
  moment: ["compass", "memory", "activity", "life", "relations"],
  support_session: ["compass", "memory", "relations"],
  knowledge_item: ["knowledge", "relations"],
  knowledge_map: ["knowledge", "relations"],
  knowledge_map_item: ["knowledge"],
  rationale: ["meaning", "compass", "relations"],
  span: ["meaning", "planning", "projects", "schedule", "relations"],
  plan_deviation: ["planning", "projects", "tasks", "schedule"],
  replan_session: ["planning", "projects", "tasks", "schedule"],
  plan_adjustment: ["planning", "projects", "tasks", "schedule"],
  protected_window: ["planning", "schedule"],
  activity_event: ["activity"],
  life_area: ["life", "relations"],
  life_schema: ["life", "relations"],
  life_item: ["life", "relations"],
  life_view: ["life"],
  relation_definition: ["relations", "life"],
  entity_link: ["relations", "life"],
};

const UNKNOWN_ENTITY_FALLBACK: readonly Slice[] = [
  "pages",
  "projects",
  "tasks",
  "routines",
  "schedule",
  "focus",
  "inbox",
  "notes",
  "compass",
  "activity",
  "memory",
  "knowledge",
  "meaning",
  "planning",
  "life",
  "relations",
];

export function slicesForSyncEntityType(entityType: string): readonly Slice[] {
  return SYNC_ENTITY_SLICES[entityType] ?? UNKNOWN_ENTITY_FALLBACK;
}

export async function invalidateForSyncChanges(
  client: QueryClient,
  entityTypes: Iterable<string>,
): Promise<void> {
  const slices = new Set<Slice>();
  const changed = [...entityTypes];
  if (changed.length) slices.add("pages");
  if (
    changed.some(
      (kind) =>
        kind === "extension_installation" ||
        kind.startsWith("life_") ||
        kind.startsWith("social_") ||
        kind.startsWith("friend") ||
        kind === "resource_grant" ||
        kind === "application_preferences" ||
        kind.startsWith("music_") ||
        kind.startsWith("lesson_") ||
        kind.startsWith("cycling_") ||
        kind.startsWith("tracking_") ||
        [
          "shared_space",
          "space_placement",
          "note_copy_offer",
          "note_copy_acceptance",
          "availability_preference",
          "note",
          "task",
          "routine",
        ].includes(kind),
    )
  ) {
    await client.invalidateQueries({
      predicate: (query) =>
        ["extensions", "extension", "social", "application-preferences"].includes(
          String(query.queryKey[0]),
        ),
    });
  }
  if (
    changed.some(
      (kind) =>
        kind.startsWith("life_") ||
        kind.startsWith("metric") ||
        kind.startsWith("rule_") ||
        kind.startsWith("notification") ||
        kind === "inbox_notification",
    )
  ) {
    await client.invalidateQueries({
      predicate: (query) => ["life", "notifications"].includes(String(query.queryKey[0])),
    });
  }
  for (const entityType of changed) {
    for (const slice of slicesForSyncEntityType(entityType)) slices.add(slice);
  }
  if (slices.size > 0) await invalidate(client, ...slices);
}

export function fetchSyncBoundary(): Promise<SyncBoundary> {
  return unwrap(api.GET("/api/v4/sync/cursor", {})) as Promise<SyncBoundary>;
}

export function ensureSyncBoundary(client: QueryClient, accountId: string): Promise<SyncBoundary> {
  return client.ensureQueryData({
    queryKey: qk.syncCursor(accountId),
    queryFn: fetchSyncBoundary,
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });
}

export function fetchSyncChanges(boundary: SyncBoundary, after: number): Promise<SyncChangePage> {
  return unwrap(
    api.GET("/api/v4/sync/changes", {
      params: {
        query: {
          after,
          limit: 200,
          schema_epoch: boundary.schema_epoch,
          reset_token: boundary.reset_token,
        },
      },
    }),
  ) as Promise<SyncChangePage>;
}

export type SyncDrainResult = {
  boundary: SyncBoundary;
  entityTypes: Set<string>;
  resetRequired: boolean;
};

export async function drainSyncFeed(
  initial: SyncBoundary,
  fetchPage: (boundary: SyncBoundary, after: number) => Promise<SyncChangePage> = fetchSyncChanges,
): Promise<SyncDrainResult> {
  let boundary = initial;
  const entityTypes = new Set<string>();

  while (true) {
    const page = await fetchPage(boundary, boundary.cursor);
    if (
      page.cursor_expired ||
      page.schema_epoch !== boundary.schema_epoch ||
      page.reset_token !== boundary.reset_token
    ) {
      return { boundary, entityTypes: new Set(), resetRequired: true };
    }

    for (const change of page.changes) entityTypes.add(change.entity_type);
    if (page.has_more && page.next_cursor <= boundary.cursor) {
      throw new Error("Sync feed did not advance its cursor.");
    }
    boundary = { ...boundary, cursor: page.next_cursor };
    if (!page.has_more) return { boundary, entityTypes, resetRequired: false };
  }
}
